// ============================================================================
// AM Collect — centralized field registry (client-side copy).
//
// Single source of truth for the field_key → canonical-target mapping that
// the AM Collect form payload uses. Consumed by:
//   1. The /admin/am-collect read-only audit UI (renders per-field outcomes).
//   2. The account card / Info tab read-path (knows which facility_field_na
//      key corresponds to which canonical column).
//
// The am-collect Edge Function keeps a Deno-compatible mirror at
//   supabase/functions/am-collect/_shared/field-registry.ts
// — sync both when adding a new field. The Edge Function CANNOT import from
// `src/` (Deno cannot resolve TS paths through Vite). See _shared/README.md.
//
// Created 2026-05-22 as part of the auto-promotion rewrite. The legacy
// review UI's field-mapping.ts re-exports this module so historical imports
// keep working.
// ============================================================================

export type TargetTable =
  | 'facility_access'
  | 'facilities'
  // 2026-09-17 — one table for a building's details, whatever its type.
  | 'facility_details'
  | 'contacts'
  | 'facility_chemicals'
  | 'facility_equipment_snapshot'
  | '_notes';

export type FieldKind =
  | 'access'
  | 'facility'
  | 'detail'
  | 'contact'
  | 'chemical'
  | 'equipment'
  | 'usables_flag'
  | 'usables_detail'
  | 'unmapped';

export type ContactSlot = 'FA' | 'BL' | 'AH';

export type EquipmentSubfield =
  | 'scrubber_count'
  | 'scrubber_brand'
  | 'vacuum_count'
  | 'vacuum_brand';

export type UsablesSubkey = 'paper' | 'liners' | 'gloves' | 'soap' | 'other';

export type ContactField = 'name' | 'email' | 'phone' | 'first_name' | 'last_name';

export interface FieldDescriptor {
  key: string;
  label: string;
  section: string;
  kind: FieldKind;
  targetColumn?: string;
  contactSlot?: ContactSlot;
  contactField?: ContactField;
  equipmentField?: EquipmentSubfield;
  usablesKey?: UsablesSubkey;
}

export const FIELD_MAP: FieldDescriptor[] = [
  // Section 1 · Contacts
  { key: 'fa_name',        label: 'Facility Admin — Name (legacy)', section: 'Contacts', kind: 'contact', contactSlot: 'FA', contactField: 'name'       },
  { key: 'fa_first_name',  label: 'Facility Admin — First name',    section: 'Contacts', kind: 'contact', contactSlot: 'FA', contactField: 'first_name' },
  { key: 'fa_last_name',   label: 'Facility Admin — Last name',     section: 'Contacts', kind: 'contact', contactSlot: 'FA', contactField: 'last_name'  },
  { key: 'fa_email',       label: 'Facility Admin — Email',         section: 'Contacts', kind: 'contact', contactSlot: 'FA', contactField: 'email'      },
  { key: 'fa_phone',       label: 'Facility Admin — Phone',         section: 'Contacts', kind: 'contact', contactSlot: 'FA', contactField: 'phone'      },
  { key: 'bl_name',        label: 'Billing — Name',                 section: 'Contacts', kind: 'contact', contactSlot: 'BL', contactField: 'name'       },
  { key: 'bl_email',       label: 'Billing — Email',                section: 'Contacts', kind: 'contact', contactSlot: 'BL', contactField: 'email'      },
  { key: 'bl_phone',       label: 'Billing — Phone',                section: 'Contacts', kind: 'contact', contactSlot: 'BL', contactField: 'phone'      },
  { key: 'ah_name',        label: 'After-hours — Name (legacy)',    section: 'Contacts', kind: 'contact', contactSlot: 'AH', contactField: 'name'       },
  { key: 'ah_first_name',  label: 'After-hours — First name',       section: 'Contacts', kind: 'contact', contactSlot: 'AH', contactField: 'first_name' },
  { key: 'ah_last_name',   label: 'After-hours — Last name',        section: 'Contacts', kind: 'contact', contactSlot: 'AH', contactField: 'last_name'  },
  { key: 'ah_email',       label: 'After-hours — Email',            section: 'Contacts', kind: 'contact', contactSlot: 'AH', contactField: 'email'      },
  { key: 'ah_phone',       label: 'After-hours — Phone',            section: 'Contacts', kind: 'contact', contactSlot: 'AH', contactField: 'phone'      },

  // Section 2 · Access & alarm
  { key: 'alarm',         label: 'Alarm code',           section: 'Access & alarm', kind: 'access', targetColumn: 'alarm_code' },
  { key: 'alarm_proc',    label: 'Arm/disarm procedure', section: 'Access & alarm', kind: 'access', targetColumn: 'arm_disarm_procedure' },
  { key: 'monitor_co',    label: 'Monitoring company',   section: 'Access & alarm', kind: 'access', targetColumn: 'monitoring_company' },
  { key: 'monitor_phone', label: 'Monitoring phone',     section: 'Access & alarm', kind: 'access', targetColumn: 'monitoring_phone' },
  { key: 'lock_type',     label: 'Lock type',            section: 'Access & alarm', kind: 'access', targetColumn: 'lock_type' },
  { key: 'lock_details',  label: 'Lock code / details',  section: 'Access & alarm', kind: 'access', targetColumn: 'lock_code' },
  { key: 'badge_info',    label: 'Badge info',           section: 'Access & alarm', kind: 'access', targetColumn: 'badge_obtain_procedure' },
  { key: 'access_notes',  label: 'Other access notes',   section: 'Access & alarm', kind: 'access', targetColumn: 'other_access_notes' },
  { key: 'cheat_sheet',   label: 'Site cheat sheet',     section: 'Access & alarm', kind: 'access', targetColumn: 'site_cheat_sheet' },

  // Section 3 · Contract & facility details
  { key: 'contract_url',  label: 'Client contract URL', section: 'Contract & details', kind: 'facility', targetColumn: 'client_contract_url' },
  // CW-Q Phase 7 / B6 (2026-08-14): `sqft_flag` retired with the column.
  // An unrecognised key now falls through to kind='unmapped', which logs and
  // writes nothing — so the 14 historical sqft_flag submissions still render in
  // the review UI, they just have no canonical target any more. TCSD-0050.
  { key: 'notes',         label: 'Facility notes',      section: 'Contract & details', kind: 'detail',   targetColumn: 'notes' },

  // Section 4 · Equipment
  { key: 'scrubber_loc',         label: 'Scrubber location',           section: 'Equipment',  kind: 'unmapped' },
  { key: 'scrubber_count',       label: 'Scrubbers on site (count)',   section: 'Equipment',  kind: 'equipment', equipmentField: 'scrubber_count' },
  { key: 'scrubber_brand',       label: 'Scrubber brand/model',        section: 'Equipment',  kind: 'equipment', equipmentField: 'scrubber_brand' },
  { key: 'vacuum_count',         label: 'Vacuums on site (count)',     section: 'Equipment',  kind: 'equipment', equipmentField: 'vacuum_count' },
  { key: 'vacuum_brand',         label: 'Vacuum brand/model',          section: 'Equipment',  kind: 'equipment', equipmentField: 'vacuum_brand' },
  { key: 'other_equipment',      label: 'Other equipment (free-text)', section: 'Equipment',  kind: 'unmapped' },

  // Section 5 · Chemicals
  { key: 'chems_on_site',        label: 'Chemicals on site (SUP-IDs)', section: 'Chemicals',  kind: 'chemical' },
  { key: 'chem_detail',          label: 'Other chemicals (free-text)', section: 'Chemicals',  kind: 'unmapped' },

  // Section 6 · Usables
  { key: 'tcs_provides_usables', label: 'TCS provides usable supplies',section: 'Usables',    kind: 'usables_flag' },
  { key: 'usable_paper',         label: 'Paper products',              section: 'Usables',    kind: 'usables_detail', usablesKey: 'paper'  },
  { key: 'usable_liners',        label: 'Trash liners',                section: 'Usables',    kind: 'usables_detail', usablesKey: 'liners' },
  { key: 'usable_gloves',        label: 'Gloves',                      section: 'Usables',    kind: 'usables_detail', usablesKey: 'gloves' },
  { key: 'usable_soap',          label: 'Soap / hand hygiene',         section: 'Usables',    kind: 'usables_detail', usablesKey: 'soap'   },
  { key: 'usable_other',         label: 'Other usable supplies',       section: 'Usables',    kind: 'usables_detail', usablesKey: 'other'  },
  { key: 'supp_low',             label: 'Critically low / reorder',    section: 'Usables',    kind: 'unmapped' },

  // Section 7 · Partner & crew
  { key: 'partner_co',        label: 'Partner company',          section: 'Partner & crew', kind: 'unmapped' },
  { key: 'partner_poc_name',  label: 'Partner POC — Name',       section: 'Partner & crew', kind: 'unmapped' },
  { key: 'partner_poc_phone', label: 'Partner POC — Phone',      section: 'Partner & crew', kind: 'unmapped' },
  { key: 'crew_access',       label: 'Crew has site access',     section: 'Partner & crew', kind: 'unmapped' },

  // Section 8 · Service basics
  { key: 'svc_days',          label: 'Service days/week',        section: 'Service basics', kind: 'unmapped' },
  { key: 'svc_window',        label: 'Service window',           section: 'Service basics', kind: 'unmapped' },
  { key: 'crew_size',         label: 'Crew size',                section: 'Service basics', kind: 'unmapped' },
];

const FIELD_INDEX: Record<string, FieldDescriptor> = Object.fromEntries(
  FIELD_MAP.map((f) => [f.key, f]),
);

export function describeField(key: string): FieldDescriptor | undefined {
  return FIELD_INDEX[key];
}

// Crew rows (crew_0_name, crew_3_phone, etc.) are dynamic.
export function describeDynamicField(key: string): FieldDescriptor | undefined {
  const m = key.match(/^crew_(\d+)_(name|phone|training)$/);
  if (m) {
    const idx = Number(m[1]) + 1;
    const sub = m[2] === 'name' ? 'Name' : m[2] === 'phone' ? 'Phone' : 'Training';
    return {
      key,
      label: 'Cleaner #' + idx + ' — ' + sub,
      section: 'Partner & crew',
      kind: 'unmapped',
    };
  }
  // Chemical chip checkboxes: chem_SUP-XXXX. These are handled by the
  // chems_on_site CSV path; per-chip booleans are also accepted.
  const cm = key.match(/^chem_SUP-(\d+)$/);
  if (cm) {
    return {
      key,
      label: 'Chemical chip: SUP-' + cm[1],
      section: 'Chemicals',
      kind: 'chemical',
    };
  }
  return undefined;
}

const SENTINEL_KEYS = new Set([
  'supplies_answered',
  'equipment_answered',
  'has_scrubber',
  'has_vacuum',
  'supp_chem',
  'supp_paper',
  'supp_soap',
  'supp_liners',
  'supp_gloves',
  'supp_other',
]);

export function isSentinel(key: string): boolean {
  return SENTINEL_KEYS.has(key);
}

export function resolveField(key: string): FieldDescriptor | null {
  if (isSentinel(key)) return null;
  return (
    describeField(key) ??
    describeDynamicField(key) ?? {
      key,
      label: key,
      section: 'Unrecognized',
      kind: 'unmapped',
    }
  );
}

export const CONTACT_ROLE_BY_SLOT: Record<ContactSlot, string> = {
  FA: 'Facility Administrator',
  BL: 'Billing',
  AH: 'After Hours Emergency',
};

// Pattern that the Edge Function uses to normalize typed-NA strings.
// AM muscle memory: typing "NA", "N/A", "None", "-", "—" all mean "not applicable".
export const NA_PATTERN = /^(NA|N\/A|None|none|-|—|n\/a|na)$/i;

// The 8 chemical supply IDs the AM Collect form ships as checkboxes.
export const CHEMICAL_SUPPLY_IDS: readonly string[] = [
  'SUP-0001',
  'SUP-0002',
  'SUP-0003',
  'SUP-0028',
  'SUP-0203',
  'SUP-0204',
  'SUP-0212',
  'SUP-0213',
] as const;
